/**
 * How much of a table may stay frozen in the box it actually got.
 *
 * Why (Jay, 2026-10-30): All Samples freezes 1,082 px on the left and 130 on the right. On half of a
 * 1920 monitor the box is about 960 px wide, so every scrolling column passed UNDER a frozen one —
 * the scrollbar moved and nothing on screen did. Many people work with two windows side by side.
 *
 * The caller lists its freeze levels from most to least, each with the width it pins; the hook
 * returns the key of the first one that still leaves `room` px for the columns that scroll. Measured
 * with a ResizeObserver on the box itself rather than the window, because the sidebar, a collapsed
 * menu and the browser zoom all change the box without the caller knowing which.
 *
 *   const level = useFreezeFit(boxRef, [{ key: 'all', width: 1212 }, { key: 'id', width: 392 }, { key: 'none', width: 0 }], 360, [tab]);
 *   <table className={`… fz-${level}`}>
 *
 * `deps` re-attaches the observer when the box is mounted later (a tab that was not on screen).
 */
import { useLayoutEffect, useState } from 'react';

export default function useFreezeFit(boxRef, levels, room, deps = []) {
  const [level, setLevel] = useState(levels[0].key);
  const signature = levels.map((l) => `${l.key}:${l.width}`).join('|');

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const pick = () => {
      const width = el.clientWidth;
      if (!width) return; // a hidden box measures 0 — keep what it had, not "freeze nothing"
      const fit = levels.find((l) => l.width + room <= width) || levels[levels.length - 1];
      setLevel((prev) => (prev === fit.key ? prev : fit.key));
    };
    pick();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', pick);
      return () => window.removeEventListener('resize', pick);
    }
    const ro = new ResizeObserver(pick);
    ro.observe(el);
    return () => ro.disconnect();
  }, [boxRef, signature, room, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return level;
}
